import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RecaptchaV2, { resetRecaptcha } from '../components/RecaptchaV2';
import { useAuthConfig } from '../hooks/useAuthConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ForgotPassword = () => {
  const { recaptchaSiteKey, recaptchaEnabled, loading: configLoading } = useAuthConfig();
  const [email, setEmail] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (recaptchaEnabled && !recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/forgot-password`, {
        email,
        recaptchaToken,
      });
      setMessage(res.data?.message || 'If an account exists, a reset link has been sent.');
      setRecaptchaToken(null);
      resetRecaptcha();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
      setRecaptchaToken(null);
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-login bg-white">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-8">
            <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mb-6" />
            <h2 className="text-[30px] font-semibold text-ink">Forgot password</h2>
            <p className="text-[16px] text-[#475467] mt-1">
              Enter your email and we&apos;ll send you a reset link.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-chip mb-5 text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="bg-primary-soft border border-primary/20 text-primary px-4 py-3 rounded-chip mb-5 text-sm">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-ink-label mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip shadow-input focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[16px] placeholder:text-ink-muted"
                placeholder="Enter your email"
                required
              />
            </div>

            {!configLoading && (
              <RecaptchaV2 siteKey={recaptchaSiteKey} onChange={setRecaptchaToken} />
            )}

            <button
              type="submit"
              disabled={loading || configLoading}
              className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 px-[18px] rounded-chip shadow-input transition-all text-[16px] disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-muted">
            <Link to="/login" className="text-primary hover:text-primary-hover font-semibold">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
